import { roundToTwoDecimalPlaces } from '@/utils/roundToTwoDecimalPlaces';

import { TrackingCalculatorEstimate } from './TrackingRequest';

// The last repayment estimate the quick calculator showed the applicant —
// purchase price, term and usage plus the cheapest lender's weekly figure
// and rates. Held as module state, not redux: the borrower slice is POSTed
// wholesale to /api/borrower/createOrUpdate, and this value exists purely for
// tracking, so it must not ride along with the application record. A session
// that saw ten quotes only ever left with the last one on screen, so a later
// record simply overwrites current — last shown wins, same as vehicleBodyType.
//
// Recorded against the session id it was shown in. The session id is a
// sliding thirty-minute window in localStorage, so a tab left open and picked
// up later starts a new session — and that session must not begin with the
// previous one's estimate on its first batch.
//
// State only: the emitter that components call is trackCalculatorEstimate in
// uniqueIdTransfer.ts, which records here and then refreshes the session fields
// already sitting in the buffer, so the estimate does not wait for the next event.
//
// What is stored is exactly the wire shape (the zod type in TrackingRequest.ts),
// so buildSessionFields can spread it without a cast. The input is looser on
// loanUsage only: the caller hands over the redux ELoanUsage string enum, which
// TypeScript will not assign to the literal union without a cast, so it comes
// in as a string and is narrowed by the guard below — anything outside the
// three values is dropped rather than recorded.
export type CalculatorEstimateSnapshot = TrackingCalculatorEstimate;

export type CalculatorEstimateInput = Omit<
    CalculatorEstimateSnapshot,
    'loanUsage'
> & {
    loanUsage: string;
};

type LoanUsage = CalculatorEstimateSnapshot['loanUsage'];

const LOAN_USAGES: readonly LoanUsage[] = ['PERSONAL', 'BUSINESS', 'NOVATED'];

// Mirror the API's bounds (Tracking.ts) so a value the API would drop the
// whole object for is not recorded here as if it had been sent.
const MAX_PURCHASE_PRICE = 10_000_000;
const MAX_LOAN_TERM_YEARS = 10;

function isLoanUsage(value: string): value is LoanUsage {
    return (LOAN_USAGES as readonly string[]).includes(value);
}

let current:
    | { sessionId: string; snapshot: CalculatorEstimateSnapshot }
    | undefined;

function roundedNumber(value: number): number {
    return Number(roundToTwoDecimalPlaces(value));
}

export function recordCalculatorEstimate(
    input: CalculatorEstimateInput,
    sessionId: string
): void {
    const {
        purchasePrice,
        loanTermYears,
        loanUsage,
        weeklyRepayment,
        advertisedRate,
        comparisonRate,
    } = input;

    if (!Number.isFinite(purchasePrice) || purchasePrice <= 0) return;
    if (purchasePrice > MAX_PURCHASE_PRICE) return;
    if (!Number.isFinite(weeklyRepayment) || weeklyRepayment <= 0) return;
    if (!Number.isFinite(loanTermYears) || loanTermYears < 1) return;
    if (loanTermYears > MAX_LOAN_TERM_YEARS) return;
    if (!isLoanUsage(loanUsage)) return;

    current = {
        sessionId,
        snapshot: {
            purchasePrice: Math.round(purchasePrice),
            loanTermYears: Math.round(loanTermYears),
            loanUsage,
            weeklyRepayment: roundedNumber(weeklyRepayment),
            ...(Number.isFinite(advertisedRate)
                ? { advertisedRate: roundedNumber(advertisedRate as number) }
                : {}),
            ...(Number.isFinite(comparisonRate)
                ? { comparisonRate: roundedNumber(comparisonRate as number) }
                : {}),
        },
    };
}

export function getTrackingCalculatorEstimate(sessionId: string): {
    calculatorEstimate?: CalculatorEstimateSnapshot;
} {
    if (!current || current.sessionId !== sessionId) return {};

    return { calculatorEstimate: current.snapshot };
}

export function resetCalculatorEstimate(): void {
    current = undefined;
}
